import { inject, Injectable } from '@angular/core';
import { isNil } from 'es-toolkit/compat';
import { forkJoin, Observable, of, take } from 'rxjs';

import { SegmentAnalysisInsightsForRestaurantDto } from '@malou-io/package-dto';
import { ApplicationLanguage, CsvAsStringArrays } from '@malou-io/package-utils';

import { LocalStorage } from '#core/storage/local-storage';
import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { SemanticAnalysisInsightsMapper } from '#modules/statistics-e-reputation/components/aggregated-e-reputation/semantic-analysis/components/aggregated-semantic-analysis-table/utils/aggregated-semantic-analysis-insights.mapper';
import { SemanticAnalysisByCategoryForCsv } from '#modules/statistics-e-reputation/models/aggregated-semantic-analysis-interface';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { formatDate } from '#shared/helpers/index';
import { DatesAndPeriod } from '#shared/models/index';

interface Data {
    dates: DatesAndPeriod;
    restaurants: LightRestaurant[];
    semanticAnalysisData: SegmentAnalysisInsightsForRestaurantDto[] | undefined;
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedSemanticAnalysisByCategoryCsvInsightsService extends AbstractCsvService<Data, Options> {
    readonly currentLang = LocalStorage.getLang();
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _getData$({ restaurants }: Options): Observable<Data> {
        return forkJoin({
            dates: of(this._aggregatedStatisticsStore.datesFilter()),
            restaurants: of(restaurants),
            semanticAnalysisData: of(this._aggregatedStatisticsStore.semanticAnalysisData()).pipe(take(1)),
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Date Start',
            'Date End',
            'Restaurant Name',
            'Number of Sentiments',
            'Total Positive Sentiment Percentage',
            'Food Positive Sentiment Percentage',
            'Service Positive Sentiment Percentage',
            'Atmosphere Positive Sentiment Percentage',
            'Price Positive Sentiment Percentage',
            'Hygiene Positive Sentiment Percentage',
        ];
    }

    protected override _getCsvDataRows({ dates, semanticAnalysisData, restaurants }: Data): CsvAsStringArrays {
        return this._buildCsvDataByCategory(dates.startDate, dates.endDate, restaurants, semanticAnalysisData).map((topic) => [
            topic.startDate,
            topic.endDate,
            topic.restaurantName,
            isNil(topic.sentimentNb) ? '' : topic.sentimentNb?.toString(),
            isNil(topic.totalPositiveSentimentPercentage) ? '' : `${topic.totalPositiveSentimentPercentage?.toString()}%`,
            isNil(topic.foodPositiveSentimentPercentage) ? '' : `${topic.foodPositiveSentimentPercentage?.toString()}%`,
            isNil(topic.servicePositiveSentimentPercentage) ? '' : `${topic.servicePositiveSentimentPercentage?.toString()}%`,
            isNil(topic.atmospherePositiveSentimentPercentage) ? '' : `${topic.atmospherePositiveSentimentPercentage?.toString()}%`,
            isNil(topic.pricePositiveSentimentPercentage) ? '' : `${topic.pricePositiveSentimentPercentage?.toString()}%`,
            isNil(topic.hygienePositiveSentimentPercentage) ? '' : `${topic.hygienePositiveSentimentPercentage?.toString()}%`,
        ]);
    }

    private _buildCsvDataByCategory(
        startDate: Date | null,
        endDate: Date | null,
        restaurants: LightRestaurant[],
        semanticAnalysisData: SegmentAnalysisInsightsForRestaurantDto[] | undefined
    ): SemanticAnalysisByCategoryForCsv[] {
        if (!semanticAnalysisData) {
            return [];
        }
        const mappedSemanticAnalysisData = SemanticAnalysisInsightsMapper.createSegmentAnalysisInsightsFromDtoArray(
            semanticAnalysisData,
            restaurants,
            this.currentLang as ApplicationLanguage
        );
        return mappedSemanticAnalysisData.map((semanticAnalysisForRestaurant) => {
            const associatedRestaurant = restaurants.find((restaurant) => restaurant.id === semanticAnalysisForRestaurant.restaurantId);
            return {
                startDate: startDate ? formatDate(startDate, false) : '',
                endDate: endDate ? formatDate(endDate, false) : '',
                restaurantName:
                    associatedRestaurant?.internalName ?? associatedRestaurant?.name ?? semanticAnalysisForRestaurant.restaurantId,
                sentimentNb: semanticAnalysisForRestaurant.sentimentNumber.value,
                totalPositiveSentimentPercentage: semanticAnalysisForRestaurant.positiveSentimentPercentage.value,
                foodPositiveSentimentPercentage: semanticAnalysisForRestaurant.foodPositiveSentimentPercentage,
                servicePositiveSentimentPercentage: semanticAnalysisForRestaurant.servicePositiveSentimentPercentage,
                atmospherePositiveSentimentPercentage: semanticAnalysisForRestaurant.atmospherePositiveSentimentPercentage,
                pricePositiveSentimentPercentage: semanticAnalysisForRestaurant.pricePositiveSentimentPercentage,
                hygienePositiveSentimentPercentage: semanticAnalysisForRestaurant.hygienePositiveSentimentPercentage,
            };
        });
    }
}
